import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { ShieldCheck, Webhook, Wallet, Code2, Layers, ArrowRight } from 'lucide-react';

const iconList = [Wallet, Webhook, ShieldCheck, Code2];
const colorKeys = ['primary', 'glow', 'success', 'accent'];
const colorClasses = {
  primary: { icon: 'text-[hsl(var(--primary))] drop-shadow-[0_0_8px_hsl(var(--primary))]', bg: 'bg-[hsl(var(--primary)/0.2)]', border: 'group-hover:border-[hsl(var(--primary)/0.6)]', glow: 'group-hover:shadow-[0_0_35px_hsl(var(--primary)/0.3)]', gradient: 'from-[hsl(var(--primary)/0.2)] to-transparent' },
  glow: { icon: 'text-[hsl(var(--primary-glow))] drop-shadow-[0_0_8px_hsl(var(--primary-glow))]', bg: 'bg-[hsl(var(--primary-glow)/0.2)]', border: 'group-hover:border-[hsl(var(--primary-glow)/0.6)]', glow: 'group-hover:shadow-[0_0_35px_hsl(var(--primary-glow)/0.3)]', gradient: 'from-[hsl(var(--primary-glow)/0.2)] to-transparent' },
  success: { icon: 'text-[hsl(var(--success))] drop-shadow-[0_0_8px_hsl(var(--success))]', bg: 'bg-[hsl(var(--success)/0.2)]', border: 'group-hover:border-[hsl(var(--success)/0.6)]', glow: 'group-hover:shadow-[0_0_35px_hsl(var(--success)/0.3)]', gradient: 'from-[hsl(var(--success)/0.2)] to-transparent' },
  accent: { icon: 'text-[hsl(var(--neon-accent))] drop-shadow-[0_0_8px_hsl(var(--neon-accent))]', bg: 'bg-[hsl(var(--neon-accent)/0.2)]', border: 'group-hover:border-[hsl(var(--neon-accent)/0.6)]', glow: 'group-hover:shadow-[0_0_35px_hsl(var(--neon-accent)/0.3)]', gradient: 'from-[hsl(var(--neon-accent)/0.2)] to-transparent' },
};

export const Features = () => {
  const { t } = useTranslation();
  const items = [
    { titleKey: 'features.feat1_title', descKey: 'features.feat1_desc' },
    { titleKey: 'features.feat2_title', descKey: 'features.feat2_desc' },
    { titleKey: 'features.feat3_title', descKey: 'features.feat3_desc' },
    { titleKey: 'features.feat4_title', descKey: 'features.feat4_desc' },
  ];

  return (
    <section id="features" data-testid="features-section" className="py-14 sm:py-20 lg:py-24 relative overflow-hidden"
      style={{ background: 'linear-gradient(180deg, hsl(258 26% 8%) 0%, hsl(262 30% 12%) 50%, hsl(258 26% 8%) 100%)' }}>
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute top-20 right-0 w-96 h-96 rounded-full bg-[hsl(var(--primary))] opacity-[0.08] blur-[100px]" />
        <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full bg-[hsl(var(--neon-accent))] opacity-[0.06] blur-[80px]" />
      </div>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-14">
          <Badge variant="outline" className="mb-4 border-[hsl(var(--primary-glow)/0.5)] text-[hsl(var(--primary-glow))] bg-[hsl(var(--primary)/0.15)] shadow-[0_0_15px_hsl(var(--primary)/0.2)]">
            <Layers className="w-3 h-3 mr-1.5" /> {t('features.badge')}
          </Badge>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-5">
            <span className="text-gradient text-neon drop-shadow-[0_0_30px_hsl(var(--primary-glow)/0.6)]">{t('features.title_line1')}</span>
            <br />
            <span className="text-white">{t('features.title_line2')}</span>
          </h2>
          <p className="text-white/80 max-w-2xl mx-auto text-base sm:text-lg">{t('features.subtitle')}</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {items.map((it, i) => {
            const c = colorClasses[colorKeys[i]];
            const Icon = iconList[i];
            return (
              <Card data-testid={`feature-card-${i}`} key={i}
                className={`group relative p-6 bg-[hsl(var(--surface)/0.9)] border-[hsl(var(--primary-glow)/0.25)] ${c.border} ${c.glow} transition-all duration-300 hover-lift cursor-default overflow-hidden shadow-[0_0_15px_hsl(var(--primary)/0.08)]`}>
                <div className={`absolute top-0 right-0 w-40 h-40 bg-gradient-to-bl ${c.gradient} rounded-bl-full opacity-40 group-hover:opacity-100 transition-opacity duration-500`} />
                <div className="relative z-10">
                  <div className={`w-14 h-14 rounded-xl ${c.bg} flex items-center justify-center mb-4 transition-all duration-300 group-hover:scale-110 shadow-[0_0_20px_hsl(var(--primary)/0.15)]`}>
                    <Icon className={`h-7 w-7 ${c.icon}`} />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2 group-hover:text-[hsl(var(--primary-glow))] group-hover:text-neon transition-all duration-300">{t(it.titleKey)}</h3>
                  <p className="text-[15px] text-white/85 leading-relaxed">{t(it.descKey)}</p>
                  <div className="mt-4 flex items-center gap-1.5 text-xs font-semibold text-[hsl(var(--primary-glow)/0.6)] group-hover:text-[hsl(var(--primary-glow))] transition-colors duration-300">
                    {t('features.explore')} <ArrowRight className="w-3 h-3 transition-transform duration-200 group-hover:translate-x-1" />
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Features;
